import "react-phone-number-input/style.css";

import { useEffect, useRef, useState } from "react";
import PhoneInput, {
  getCountryCallingCode,
  getCountries,
  isValidPhoneNumber,
  parsePhoneNumber,
} from "react-phone-number-input";
import es from "react-phone-number-input/locale/es";

import { cn } from "~/lib/utils";

import type { ClipboardEvent, CSSProperties, FocusEvent, KeyboardEvent } from "react";
import type { Country } from "react-phone-number-input";

// Re-export so forms validate with the same library that powers the input.
export { isValidPhoneNumber };

type PhoneFieldProps = {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  id?: string;
  invalid?: boolean;
  className?: string;
  enterKeyHint?: "done" | "enter" | "go" | "next" | "previous" | "search" | "send";
  autoComplete?: string;
  nextFocusId?: string;
  focusOnMount?: boolean;
  detectCountry?: boolean;
  placeholder?: string;
};

const DEFAULT_COUNTRY: Country = "AR";
const SUPPORTED_COUNTRIES = new Set(getCountries());

const TIMEZONE_COUNTRY: Record<string, Country> = {
  // IANA can return either the canonical Argentina/* identifiers or these legacy aliases,
  // depending on the browser/OS timezone database version.
  "America/Buenos_Aires": "AR",
  "America/Catamarca": "AR",
  "America/Cordoba": "AR",
  "America/Jujuy": "AR",
  "America/Mendoza": "AR",
  "America/Rosario": "AR",
  "America/Argentina/Buenos_Aires": "AR",
  "America/Argentina/Catamarca": "AR",
  "America/Argentina/Cordoba": "AR",
  "America/Argentina/Jujuy": "AR",
  "America/Argentina/La_Rioja": "AR",
  "America/Argentina/Mendoza": "AR",
  "America/Argentina/Rio_Gallegos": "AR",
  "America/Argentina/Salta": "AR",
  "America/Argentina/San_Juan": "AR",
  "America/Argentina/San_Luis": "AR",
  "America/Argentina/Tucuman": "AR",
  "America/Argentina/Ushuaia": "AR",
  "Argentina/Buenos_Aires": "AR",
  "Etc/GMT+3": "AR",
  "America/Asuncion": "PY",
  "America/Bogota": "CO",
  "America/Caracas": "VE",
  "America/Costa_Rica": "CR",
  "America/El_Salvador": "SV",
  "America/Guatemala": "GT",
  "America/Guayaquil": "EC",
  "America/Havana": "CU",
  "America/La_Paz": "BO",
  "America/Lima": "PE",
  "America/Managua": "NI",
  "America/Mexico_City": "MX",
  "America/Montevideo": "UY",
  "America/New_York": "US",
  "America/Chicago": "US",
  "America/Denver": "US",
  "America/Phoenix": "US",
  "America/Los_Angeles": "US",
  "America/Anchorage": "US",
  "Pacific/Honolulu": "US",
  "America/Panama": "PA",
  "America/Puerto_Rico": "PR",
  "America/Santiago": "CL",
  "America/Santo_Domingo": "DO",
  "America/Sao_Paulo": "BR",
  "America/Tegucigalpa": "HN",
};

/**
 * Detección pasiva: usa sólo región declarada por el navegador y zona horaria.
 * No solicita ubicación, no lee coordenadas y no envía la IP a terceros.
 */
export function resolvePhoneCountry(locales: readonly string[], timeZone?: string): Country {
  // Para aproximar ubicación física, una zona horaria reconocida es una señal mejor que el
  // idioma: mucha gente en Argentina conserva Chrome en `en-US`, que no significa que esté en EE.UU.
  if (timeZone && TIMEZONE_COUNTRY[timeZone]) return TIMEZONE_COUNTRY[timeZone];

  for (const locale of locales) {
    try {
      const region = new Intl.Locale(locale).region?.toUpperCase();
      if (region && SUPPORTED_COUNTRIES.has(region as Country)) return region as Country;
    } catch {
      // Ignorar locales malformados e intentar la siguiente señal.
    }
  }

  return DEFAULT_COUNTRY;
}

function detectBrowserCountry(): Country {
  if (typeof navigator === "undefined") return DEFAULT_COUNTRY;

  const locales = navigator.languages?.length ? navigator.languages : [navigator.language];
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return resolvePhoneCountry(locales.filter(Boolean), timeZone);
}

/**
 * Phone input with a country selector (Argentina when passive detection has no signal).
 * Outputs a canonical E.164 string (e.g. "+5491123500639") regardless of how the user types
 * the national part (with/without the leading 0, the "15", or the mobile "9"). The backend
 * re-normalizes too, so this is the UX layer — flag + as-you-type formatting + validation.
 *
 * The editable input shows only the national number: the selected country's prefix is metadata,
 * not text where the caret can land. The wrapper matches the landing inputs.
 */
/**
 * Un valor que es SÓLO el prefijo internacional del país elegido ("+54" con Argentina, "+"
 * a mitad de tipear, …) no es un número: es el país, que este campo dibuja aparte al lado de
 * la banderita.
 *
 * Si llega igual como `value`, el input enmascarado lo escribe literal adentro. Dos caminos
 * distintos llegan a un "+54" así:
 * - Un formulario sembró el campo con "+54" para "ayudar" (pasó de verdad, dos veces) — hay
 *   que descartarlo.
 * - La persona está TIPEANDO: la librería antepone el código del país elegido y le va sumando
 *   cada dígito nacional, así que el primer dígito de un número real con Argentina elegida da
 *   "+541", el segundo (código de área "11", Buenos Aires) da "+5411". Ninguno de los dos es
 *   "sólo el prefijo": ya hay número adentro. Confundir esto con lo anterior fue el bug real —
 *   un límite genérico de "hasta 4 dígitos" trataba el código de área como si todavía fuera el
 *   prefijo y lo borraba en cada tecla, así que area codes de 2 dígitos (justo "11") no se
 *   podían terminar de escribir en el celular.
 *
 * El código de país que la librería antepone nunca lo tipea la persona: es SIEMPRE el del país
 * ya elegido. Por eso "sólo prefijo" se resuelve contra ESE código — apenas los dígitos después
 * del "+" superan su longitud, es número, no prefijo, sin importar cuántos dígitos sean.
 */
export function stripBareCallingCode(
  value: string | undefined,
  callingCode: string,
): string | undefined {
  if (!value) return value;
  const match = value.trim().match(/^\+(\d*)$/);
  if (!match) return value;
  return callingCode.startsWith(match[1]) ? undefined : value;
}

export function PhoneField({
  value: rawValue,
  onChange,
  id,
  invalid,
  className,
  enterKeyHint = "next",
  autoComplete = "tel",
  nextFocusId,
  focusOnMount = false,
  detectCountry = true,
  placeholder = "Tu número",
}: PhoneFieldProps) {
  const [country, setCountry] = useState<Country>(DEFAULT_COUNTRY);
  const value = stripBareCallingCode(rawValue, getCountryCallingCode(country));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (focusOnMount) inputRef.current?.focus();
  }, [focusOnMount]);

  useEffect(() => {
    let valueCountry: Country | undefined;
    try {
      valueCountry = value ? parsePhoneNumber(value)?.country : undefined;
    } catch {
      valueCountry = undefined;
    }
    if (valueCountry) {
      setCountry(valueCountry);
      return;
    }
    if (detectCountry) setCountry(detectBrowserCountry());
  }, [detectCountry, value]);

  // Pasting a number that already contains the country code (e.g. "+54 9 11..." copied from
  // WhatsApp, a compact "+5491123456789", or a foreign "+34...") confuses the masked input,
  // which would otherwise drop everything down to just "+54". Intercept the paste, parse it with
  // the same library, and set the canonical E.164 value directly so the flag + national part fill
  // in correctly. If it can't be parsed, fall through to the default behaviour.
  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData?.getData("text")?.trim();
    if (!text) return;
    let parsed;
    try {
      parsed = parsePhoneNumber(text, "AR");
    } catch {
      parsed = undefined;
    }
    if (parsed?.number) {
      event.preventDefault();
      onChange(parsed.number);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter" || !nextFocusId) return;
    const nextControl = document.getElementById(nextFocusId);
    if (!nextControl) return;
    event.preventDefault();
    nextControl.focus();
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    // Algunos teclados mobile colocan el caret al inicio al llegar con “Siguiente”.
    // El campo ya contiene sólo el número nacional, pero mantenerlo al final evita igualmente
    // insertar dígitos delante de un valor autocompletado.
    const input = event.currentTarget;
    requestAnimationFrame(() => input.setSelectionRange(input.value.length, input.value.length));
  };

  return (
    <PhoneInput
      defaultCountry={country}
      international={false}
      addInternationalOption={false}
      labels={es}
      value={value}
      onChange={onChange}
      onCountryChange={(nextCountry) => {
        if (nextCountry) setCountry(nextCountry);
      }}
      style={
        {
          "--phone-country-prefix": `"+${getCountryCallingCode(country)}"`,
        } as CSSProperties
      }
      autoComplete={autoComplete}
      countrySelectProps={{
        tabIndex: -1,
        "aria-label": "País del número de teléfono",
      }}
      numberInputProps={{
        ref: inputRef,
        id,
        onPaste: handlePaste,
        onKeyDown: handleKeyDown,
        onFocus: handleFocus,
        enterKeyHint,
        inputMode: "tel",
        placeholder,
        "aria-label": "Número de teléfono",
        className:
          "min-w-0 flex-1 bg-transparent text-[15px] text-landing-text outline-none placeholder:text-landing-light",
      }}
      className={cn(
        "academia-phone flex items-center gap-2 rounded-[6px] border bg-white px-3 py-2.5 transition-colors focus-within:ring-1 focus-within:ring-landing-red/30",
        invalid ? "border-landing-red" : "border-landing-border focus-within:border-landing-red",
        className,
      )}
    />
  );
}
